import { useState, useEffect } from 'react';
import { Flag, Search, Check, Trash2 } from 'lucide-react';
import { frameOrderApi } from '../../services/frameOrderApi';
import { FrameOrderComments } from './FrameOrderComments';
import type { FrameOrderDetail as FrameOrderDetailType } from '../../types/frameOrders';

interface FrameOrderDetailProps {
    documentId: string;
    canEdit: boolean;
    onCommentAdded: () => void;
    assigneeOptions: string[];
}

function formatDate(dateStr: string | null): string {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('de-CH');
}

function formatNumber(num: number | null | undefined): string {
    if (num == null) return '-';
    return Math.round(num).toLocaleString('de-CH');
}

// Tooltips for fields whose label alone is ambiguous. Keyed by the label text.
const FIELD_HINTS: Record<string, string> = {
    // QtyOnStock is aggregated per company and product across every location, so it is
    // not the stock at the location shown on this line. Same figure on every line of a
    // product that is stored in more than one warehouse.
    'Stock (all locations)': 'Stock of this product across all locations of the company - not the stock at the location of this line.',
};

function FollowUpSection({ detail, canEdit, assigneeOptions, onChanged }: {
    detail: FrameOrderDetailType;
    canEdit: boolean;
    assigneeOptions: string[];
    onChanged: () => void;
}) {
    const hasFollowUp = detail.HasFollowUp;
    const [assignee, setAssignee] = useState(detail.FollowUpAssignee || '');
    const [searchText, setSearchText] = useState(detail.FollowUpAssignee || '');
    const [showDropdown, setShowDropdown] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        setAssignee(detail.FollowUpAssignee || '');
        setSearchText(detail.FollowUpAssignee || '');
    }, [detail.FollowUpAssignee]);

    const filteredOptions = assigneeOptions.filter(name =>
        name.toLowerCase().includes(searchText.toLowerCase())
    );

    const handleSelectAssignee = (name: string) => {
        setAssignee(name);
        setSearchText(name);
        setShowDropdown(false);
    };

    const handleCreate = async () => {
        if (submitting) return;
        setSubmitting(true);
        try {
            await frameOrderApi.followUpAction(detail.DocumentID, 'create', assignee || null);
            onChanged();
        } catch (err) {
            console.error('Failed to create follow-up:', err);
        } finally {
            setSubmitting(false);
        }
    };

    const handleUpdate = async () => {
        if (submitting) return;
        setSubmitting(true);
        try {
            await frameOrderApi.followUpAction(detail.DocumentID, 'update', assignee || null);
            onChanged();
        } catch (err) {
            console.error('Failed to update follow-up:', err);
        } finally {
            setSubmitting(false);
        }
    };

    const handleRemove = async () => {
        if (submitting) return;
        setSubmitting(true);
        try {
            await frameOrderApi.followUpAction(detail.DocumentID, 'remove');
            onChanged();
        } catch (err) {
            console.error('Failed to remove follow-up:', err);
        } finally {
            setSubmitting(false);
        }
    };

    const assigneeChanged = assignee !== (detail.FollowUpAssignee || '');

    return (
        <div className="mb-4">
            <h4 className="mb-2 text-sm font-semibold text-gray-700 flex items-center gap-1.5">
                <Flag className="h-4 w-4 text-orange-500" fill={hasFollowUp ? 'currentColor' : 'none'} />
                Follow-up
            </h4>

            {hasFollowUp ? (
                <div className="rounded-md border border-orange-200 bg-orange-50/50 p-3">
                    <div className="flex items-center gap-1.5 text-xs text-orange-700">
                        <Flag className="h-3.5 w-3.5" fill="currentColor" />
                        <span className="font-medium">Active</span>
                    </div>

                    {canEdit && (
                        <>
                            <div className="relative mt-2">
                                <label className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-gray-500">
                                    Assigned to
                                </label>
                                <div className="relative">
                                    <Search className="pointer-events-none absolute left-2 top-1.5 h-3.5 w-3.5 text-gray-400" />
                                    <input
                                        type="text"
                                        value={searchText}
                                        onChange={(e) => {
                                            setSearchText(e.target.value);
                                            setAssignee(e.target.value);
                                            setShowDropdown(true);
                                        }}
                                        onFocus={() => setShowDropdown(true)}
                                        onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                                        placeholder="Search employee..."
                                        className="h-7 w-full rounded border border-gray-300 bg-white pl-7 pr-2 text-xs text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                    />
                                </div>
                                {showDropdown && filteredOptions.length > 0 && (
                                    <div className="absolute z-30 mt-0.5 max-h-32 w-full overflow-y-auto rounded border border-gray-200 bg-white shadow-md">
                                        {filteredOptions.map(name => (
                                            <button
                                                key={name}
                                                onMouseDown={() => handleSelectAssignee(name)}
                                                className={`w-full px-2 py-1 text-left text-xs hover:bg-blue-50 ${
                                                    name === assignee ? 'bg-blue-50 font-medium text-blue-700' : 'text-gray-700'
                                                }`}
                                            >
                                                {name}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className="mt-2 flex gap-1.5">
                                <button
                                    onClick={handleUpdate}
                                    disabled={submitting || !assigneeChanged}
                                    className="inline-flex h-7 flex-1 items-center justify-center gap-1 rounded-md bg-orange-500 text-xs font-medium text-white hover:bg-orange-600 disabled:opacity-50"
                                >
                                    <Check className="h-3 w-3" /> Update
                                </button>
                                <button
                                    onClick={handleRemove}
                                    disabled={submitting}
                                    className="inline-flex h-7 items-center justify-center gap-1 rounded-md border border-red-200 bg-red-50 px-2 text-xs font-medium text-red-700 hover:bg-red-100 disabled:opacity-50"
                                >
                                    <Trash2 className="h-3 w-3" /> Remove
                                </button>
                            </div>
                        </>
                    )}

                    {!canEdit && detail.FollowUpAssignee && (
                        <p className="mt-1 text-xs text-gray-600">
                            Assigned to: <span className="font-medium">{detail.FollowUpAssignee}</span>
                        </p>
                    )}
                </div>
            ) : canEdit ? (
                <div className="rounded-md border border-gray-200 bg-gray-50/50 p-3">
                    <p className="text-xs text-gray-500 mb-2">No follow-up set for this order.</p>
                    <div className="relative mb-2">
                        <label className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-gray-500">
                            Assigned to
                        </label>
                        <div className="relative">
                            <Search className="pointer-events-none absolute left-2 top-1.5 h-3.5 w-3.5 text-gray-400" />
                            <input
                                type="text"
                                value={searchText}
                                onChange={(e) => {
                                    setSearchText(e.target.value);
                                    setAssignee(e.target.value);
                                    setShowDropdown(true);
                                }}
                                onFocus={() => setShowDropdown(true)}
                                onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                                placeholder="Search employee..."
                                className="h-7 w-full rounded border border-gray-300 bg-white pl-7 pr-2 text-xs text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                        </div>
                        {showDropdown && filteredOptions.length > 0 && (
                            <div className="absolute z-30 mt-0.5 max-h-32 w-full overflow-y-auto rounded border border-gray-200 bg-white shadow-md">
                                {filteredOptions.map(name => (
                                    <button
                                        key={name}
                                        onMouseDown={() => handleSelectAssignee(name)}
                                        className={`w-full px-2 py-1 text-left text-xs hover:bg-blue-50 ${
                                            name === assignee ? 'bg-blue-50 font-medium text-blue-700' : 'text-gray-700'
                                        }`}
                                    >
                                        {name}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    <button
                        onClick={handleCreate}
                        disabled={submitting}
                        className="inline-flex h-7 w-full items-center justify-center gap-1.5 rounded-md bg-orange-500 text-xs font-medium text-white hover:bg-orange-600 disabled:opacity-50"
                    >
                        <Flag className="h-3 w-3" /> Set follow-up
                    </button>
                </div>
            ) : (
                <p className="text-xs text-gray-400">No follow-up.</p>
            )}
        </div>
    );
}

export function FrameOrderDetail({ documentId, canEdit, onCommentAdded, assigneeOptions }: FrameOrderDetailProps) {
    const [detail, setDetail] = useState<FrameOrderDetailType | null>(null);
    const [loading, setLoading] = useState(true);

    const loadDetail = () => {
        setLoading(true);
        frameOrderApi.get(documentId)
            .then(setDetail)
            .catch(err => console.error('Failed to load detail:', err))
            .finally(() => setLoading(false));
    };

    useEffect(() => { loadDetail(); }, [documentId]);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-8">
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
            </div>
        );
    }

    if (!detail) {
        return <p className="py-4 text-sm text-gray-500">Failed to load order details.</p>;
    }

    const fields = [
        { label: 'Document ID', value: detail.DocumentID },
        { label: 'Company', value: detail.CompanyCode },
        { label: 'Document Type', value: detail.DocumentType },
        { label: 'Customer', value: `${detail.CustomerName} (${detail.CustomerCode})` },
        { label: 'Product', value: `${detail.ProductName} (${detail.ProductCode})` },
        { label: 'Location', value: `${detail.LocationName} (${detail.LocationCode})` },
        { label: 'Coordinator', value: detail.SalesCoordinatorName || '-' },
        { label: 'Quantity', value: `${formatNumber(detail.Quantity)} ${detail.UnitOfMeasureCode}` },
        { label: 'Outstanding', value: `${formatNumber(detail.OutstandingQuantity)} (${detail.PctOutstanding.toFixed(1)}%)` },
        { label: 'Stock (all locations)', value: formatNumber(detail.QtyOnStock) },
        { label: 'Qty to Ship', value: formatNumber(detail.QtyToShip) },
        { label: 'Unit Price', value: detail.UnitPrice ? `${detail.UnitPrice.toFixed(2)}` : '-' },
        { label: 'Line Amount', value: detail.LineAmount ? `${detail.LineAmount.toFixed(2)}` : '-' },
        { label: 'Posting/Order Date', value: formatDate(detail.PostingOrOrderDate) },
        { label: 'Order Date', value: formatDate(detail.OrderDate) },
        { label: 'Expiry Date', value: formatDate(detail.ExpiryDate) },
        { label: 'Shipment Date', value: formatDate(detail.ShipmentDate) },
        { label: 'Last Invoice', value: formatDate(detail.LastSalesInvoice) },
        { label: 'Date First Call', value: formatDate(detail.DateFirstCall) },
        { label: 'Date Last Call', value: formatDate(detail.DateLastCall) },
        { label: 'Lot No', value: detail.LotNo || '-' },
        { label: 'Ship-to Country', value: detail.ShipToCountryRegion || '-' },
        { label: 'Shipment Method', value: detail.ShipmentMethodCode || '-' },
    ];

    return (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2">
                <h4 className="mb-3 text-sm font-semibold text-gray-700">Order Details</h4>
                <div className="grid grid-cols-2 gap-x-6 gap-y-1 sm:grid-cols-3">
                    {fields.map(f => (
                        <div key={f.label} className="py-1">
                            <dt className="text-xs text-gray-500" title={FIELD_HINTS[f.label]}>{f.label}</dt>
                            <dd className="text-sm text-gray-900">{f.value}</dd>
                        </div>
                    ))}
                </div>
            </div>
            <div>
                <FollowUpSection
                    detail={detail}
                    canEdit={canEdit}
                    assigneeOptions={assigneeOptions}
                    onChanged={() => { loadDetail(); onCommentAdded(); }}
                />
                <FrameOrderComments
                    documentId={documentId}
                    comments={detail.comments}
                    canEdit={canEdit}
                    onCommentAdded={() => { loadDetail(); onCommentAdded(); }}
                />
            </div>
        </div>
    );
}
